/**
 * Which schedule refusals a submission renders (prod fixes 2026-10-02, PLAN.md C2; integration round 2,
 * INT1-P2-02).
 *
 * professorx refuses a website schedule with a typed 4xx (410 `UPLOAD_EXPIRED`, later T8's 422
 * `UNSUPPORTED_INPUT`) ONLY when the request lists that code in its `acceptsRefusals` multipart parts.
 * Bundles from before this fix send none and are admitted exactly as before, because they treat any 4xx
 * as terminal (remove the card, delete its upload, show a generic "Error").
 *
 * A code is listed only by the surface that renders it: the converter's `processFileWithFeedback`
 * (tool page Convert and the Re-edit modal's Apply & Restart; X2-3 / X2-4 / X2-5, and T8's "Can't convert"). Editors, the Studio
 * and stored-job replays do not, so they keep today's path. The marking rides on the FileUpload
 * INSTANCE (a WeakMap, never persisted): the hands-off ladder's retries re-send it, a reloaded card does
 * not.
 */
export const ACCEPTS_REFUSALS_PART = 'acceptsRefusals';

/** The codes the converter renders: 410 UPLOAD_EXPIRED (T3: X2-3/X2-4/X2-5) and 422 UNSUPPORTED_INPUT (T8: "Can't convert" on the row). */
export const CONVERTER_RENDERED_REFUSALS: readonly string[] = Object.freeze(['UPLOAD_EXPIRED', 'UNSUPPORTED_INPUT']);

const accepted = new WeakMap<object, readonly string[]>();

export function markRefusalsRendered(fileUpload: object | null | undefined, codes: readonly string[] | null | undefined) {
    if (!fileUpload || !codes || codes.length === 0) return;
    accepted.set(fileUpload, codes.filter(c => typeof c === 'string' && /^[A-Z][A-Z0-9_]{0,39}$/.test(c)));
}

export function renderedRefusalsOf(fileUpload: object | null | undefined): readonly string[] {
    return (fileUpload && accepted.get(fileUpload)) || [];
}

/** One part per code (professorx binds a List<String>); nothing at all for an unmarked submission. */
export function appendRenderedRefusals(form: FormData, fileUpload: object | null | undefined) {
    renderedRefusalsOf(fileUpload).forEach(code => form.append(ACCEPTS_REFUSALS_PART, code));
}

/**
 * gif-maker (wave 2 ERRORS): a submission whose caller renders enforcement refusals (429 / 402) itself. FileProcessService
 * then tags the error as before but does NOT open the global "You've hit a limit" modal (the editor's offer showed a
 * second dialog on top of it). Instance-scoped like `markRefusalsRendered`; nothing changes for unmarked submissions.
 */
const rendersEnforcement = new WeakSet<object>();

export function markEnforcementRendered(fileUpload: object | null | undefined) {
    if (fileUpload) rendersEnforcement.add(fileUpload);
}

export function enforcementRenderedBy(fileUpload: object | null | undefined): boolean {
    return !!fileUpload && rendersEnforcement.has(fileUpload);
}
